import { createClient } from '@supabase/supabase-js';

// Supabase configuration
// Using environment variables for secure configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create Supabase client with options
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Helper functions for Supabase operations
export const getUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      console.warn('Error getting user:', error.message);
      return null;
    }
    return data.user;
  } catch (error) {
    console.warn('Unexpected error getting user:', error.message);
    return null;
  }
};

export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn('Error getting session:', error.message);
      return null;
    }
    return data.session;
  } catch (error) {
    console.warn('Unexpected error getting session:', error.message);
    return null;
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Unexpected error signing out:', error.message);
    return false;
  }
};

// Auth helpers
export const signUp = async (email, password) => {
  // Disable email confirmation for testing purposes
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      // This will automatically sign in the user after registration
      // without requiring email confirmation
      data: {
        email_confirmed: true
      }
    }
  });
  
  if (error) throw error;
  
  // If no session was created, manually sign in the user
  if (!data.session) {
    return signIn(email, password);
  }
  
  return data;
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (error) throw error;
  return data;
};

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  if (error) throw error;
  return data;
};

// Storage helpers
export const getFileUrl = (bucket, path) => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

export const uploadFile = async (bucket, path, file) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });
  if (error) throw error;
  return data;
};

export const downloadFile = async (bucket, path) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .download(path);
  if (error) throw error;
  return data;
};

// Database helpers
export const fetchProfile = async (userId) => {
  try {
    if (!userId) {
      console.warn('Cannot fetch profile: userId is undefined or null');
      return null;
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (error) {
      // If the error is "No rows found", it means the profile doesn't exist yet
      if (error.code === 'PGRST116') {
        console.warn(`No profile found for user ${userId}`);
        return null;
      }
      
      console.error('Error fetching profile:', error.message);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Unexpected error fetching profile:', error.message);
    return null;
  }
};

export const updateProfile = async (userId, updates) => {
  try {
    if (!userId) {
      console.warn('Cannot update profile: userId is undefined or null');
      return null;
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating profile:', error.message);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Unexpected error updating profile:', error.message);
    return null;
  }
};

// Database schema setup (for reference)
/*
-- Create profiles table
create table profiles (
  id uuid references auth.users on delete cascade primary key,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null,
  username text unique,
  full_name text,
  avatar_url text
);

-- Create voice_models table
create table voice_models (
  id uuid default uuid_generate_v4() primary key,
  user_id uuid references auth.users on delete cascade not null,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null,
  sample_paths text[] not null,
  status text not null check (status in ('processing', 'ready', 'error')),
  error_message text
);

-- Create covers table
create table covers (
  id uuid default uuid_generate_v4() primary key,
  user_id uuid references auth.users on delete cascade not null,
  voice_model_id uuid references voice_models on delete cascade not null,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  updated_at timestamp with time zone default timezone('utc'::text, now()) not null,
  youtube_url text not null,
  title text,
  cover_url text,
  status text not null check (status in ('processing', 'completed', 'error')),
  error_message text
);

-- Create storage buckets
insert into storage.buckets (id, name) values ('voice-samples', 'voice-samples');
insert into storage.buckets (id, name) values ('covers', 'covers');

-- Set up storage policies
create policy "Voice samples are accessible by the owner" 
on storage.objects for select 
using (bucket_id = 'voice-samples' and auth.uid() = (storage.foldername(name))[1]::uuid);

create policy "Covers are accessible by anyone" 
on storage.objects for select 
using (bucket_id = 'covers');
*/
